import { LoadingStates } from '@pb/utils'
import produce from 'immer'
import { propOr, mergeDeepRight } from 'ramda'
import { createReducer } from 'redux-act'

import { DELIVERY_METHODS, CHECKOUT_SECTIONS } from 'features/checkout/constants'

import { NormalizedDeliveryModes, CheckoutState } from '../types'
import * as actions from './actions'
import initialState from './initialState'
import { normalizePickupPlaces } from './normalize'

// @ts-expect-error FIXME type {} is not assignable to CustomerState
const reducer = createReducer<CheckoutState>({}, initialState)

reducer.on(actions.setShouldShowErrorScreen, (state, shouldShowErrorScreen) => ({
  ...state,
  shouldShowErrorScreen,
}))
// @ts-expect-error FIXME type {} is not assignable to CustomerState
reducer.on(actions.resetToInitialState, () => initialState)
reducer.on(actions.postPrepareStart, (state) => ({
  ...state,
  prepareStatus: LoadingStates.Loading,
}))
reducer.on(actions.postPrepareSuccess, (state, prepare?: Object) => ({
  ...state,
  prepareStatus: LoadingStates.LoadedSuccess,
  ...prepare,
}))

reducer.on(actions.postPrepareFail, (state) => ({
  ...state,
  prepareStatus: LoadingStates.LoadedFail,
}))

reducer.on(actions.setError, (state, error) => ({
  ...state,
  errors: [...state.errors, error],
}))
reducer.on(actions.clearErrorByCode, (state, code) => ({
  ...state,
  errors: (propOr as any)([], 'errors', state).filter((error) => error.code !== code),
}))
reducer.on(actions.clearErrorByTarget, (state, target) => ({
  ...state,
  errors: (propOr as any)([], 'errors', state).filter((error) => error.target !== target),
}))

reducer.on(actions.setDeliveryPhoneNumber, (state, { prefix, cellPhone }) => ({
  ...state,
  delivery: {
    ...state.delivery,
    phoneNumber: {
      prefix,
      cellPhone,
    },
  },
}))

reducer.on(actions.updateCustomerVatNumber, (state, vatNumber) => ({
  ...state,
  additionalInfo: {
    ...state.additionalInfo,
    customerVatNumber: vatNumber,
  },
}))
reducer.on(actions.updateVatInvoiceType, (state, vatInvoiceType) => ({
  ...state,
  additionalInfo: {
    ...state.additionalInfo,
    customerVatInvoiceType: vatInvoiceType,
  },
}))
reducer.on(actions.setSelectedDeliverySpeed, (state, deliverySpeed) => ({
  ...state,
  errors: [],
  delivery: {
    ...state.delivery,
    selectedDeliverySpeed: deliverySpeed,
    isCustomerSelectedSpeed: true,
  },
}))
reducer.on(actions.setSelectedDeliveryAddress, (state, deliveryAddress) => ({
  ...state,
  cart: {
    ...state.cart,
    deliveryAddress,
  },
}))
reducer.on(actions.setAvailableDeliverySpeeds, (state, availableDeliverySpeeds) => ({
  ...state,
  delivery: {
    ...state.delivery,
    availableDeliverySpeeds,
  },
}))

reducer.on(actions.setSelectedPickupPlacePerParcel, (state, { parcelId, selectedPickupPlace }) => ({
  ...state,
  delivery: {
    ...state.delivery,
    deliveryMethods: {
      ...state.delivery.deliveryMethods,
      selectedPickupPlacePerParcel: {
        ...state.delivery.deliveryMethods?.selectedPickupPlacePerParcel,
        ...(parcelId && selectedPickupPlace && { [parcelId]: selectedPickupPlace }),
      },
    },
  },
}))

reducer.on(actions.setSelectedPickupPoint, (state, selectedPickupPoint) => ({
  ...state,
  delivery: {
    ...state.delivery,
    deliveryMethods: {
      ...state.delivery.deliveryMethods,
      [DELIVERY_METHODS.pickupPoint]: {
        ...state.delivery.deliveryMethods?.[DELIVERY_METHODS.pickupPoint],
        selectedPickupPoint,
      },
    },
  },
}))

reducer.on(actions.setSelectedCnCPoint, (state, selectedCnCPoint) => ({
  ...state,
  delivery: {
    ...state.delivery,
    deliveryMethods: {
      ...state.delivery.deliveryMethods,
      [DELIVERY_METHODS.clickAndCollect]: {
        ...state.delivery.deliveryMethods?.[DELIVERY_METHODS.clickAndCollect],
        selectedCnCPoint,
      },
    },
  },
}))
reducer.on(actions.setSelectedDeliveryMethod, (state, deliveryMethod) => ({
  ...state,
  errors: [],
  delivery: {
    ...state.delivery,
    selectedDeliveryMethod: deliveryMethod,
  },
}))
reducer.on(actions.setOutOfStockItems, (state, outOfStockItems) => ({
  ...state,
  outOfStockItems,
}))
reducer.on(actions.clearOutOfStockItems, (state) => ({
  ...state,
  outOfStockItems: [],
}))
// ----------------------------------------
// FETCH CONTEXT
// ----------------------------------------)

// @ts-expect-error FIXME type {} is not assignable to CustomerState
reducer.on(actions.fetchContextStart, (state) => ({
  ...state,
  ...initialState,
  prepareStatus: LoadingStates.LoadedSuccess,
  contextStatus: LoadingStates.Loading,
  settings: state.settings,
}))
export const fetchContextSuccess = (
  state: CheckoutState,
  { paymentData, delivery, payment, customer, cart, orderTotals, errors }: CheckoutState,
): CheckoutState => {
  return {
    ...state,
    contextStatus: LoadingStates.LoadedSuccess,
    paymentData: {
      ...state.paymentData,
      ...paymentData,
    },
    payment: {
      ...state.payment,
      ...payment,
    },
    delivery: {
      ...state.delivery,
      ...delivery,
    },
    customer: {
      ...state.customer,
      ...customer,
    },
    cart: {
      ...state.cart,
      ...cart,
    },
    orderTotals,
    ...(errors && { errors: [...state.errors, ...errors] }),
  }
}
reducer.on(actions.fetchContextSuccess, fetchContextSuccess)

reducer.on(actions.fetchContextFail, (state, payload) => {
  const { shouldShowErrorScreen = true } = payload

  return {
    ...state,
    shouldShowErrorScreen,
    contextStatus: LoadingStates.LoadedFail,
  }
})

// ----------------------------------------
// UPDATE CUSTOMER
// ----------------------------------------
reducer.on(actions.updateCustomerDetailsReset, (state) => {
  return {
    ...state,
    customerDetailsStatus: LoadingStates.NotLoaded,
  }
})

reducer.on(actions.updateCustomerDetailsStart, (state) => {
  return {
    ...state,
    customerDetailsStatus: LoadingStates.Loading,
  }
})
reducer.on(actions.updateCustomerDetailsSuccess, (state, { cart, customer }) => {
  return {
    ...state,
    customerDetailsStatus: LoadingStates.LoadedSuccess,
    cart: {
      ...state.cart,
      ...cart,
    },
    customer: {
      ...state.customer,
      ...customer,
    },
    errors: (propOr as any)([], 'errors', state).filter(
      (error) => error.section !== CHECKOUT_SECTIONS.myInformation,
    ),
  }
})
reducer.on(actions.updateCustomerDetailsFail, (state, errors) => {
  return {
    ...state,
    customerDetailsStatus: LoadingStates.LoadedFail,
    errors,
  }
})
// ----------------------------------------
// UPDATE CART DATA
// ----------------------------------------)
reducer.on(actions.updateCart, (state, { cart }) => {
  return {
    ...state,
    cart: {
      ...state.cart,
      ...cart,
    },
  }
})

// ----------------------------------------
// UPDATE DELIVERY DATA
// ----------------------------------------
reducer.on(actions.updateDelivery, (state, { delivery }) => {
  return {
    ...state,
    delivery: {
      ...state.delivery,
      ...delivery,
    },
  }
})

// ----------------------------------------
// PATCH DELIVERY
// ----------------------------------------
reducer.on(actions.selectDeliverySpeedStart, (state) => ({
  ...state,
  errors: [],
  deliverySpeedSelectionStatus: LoadingStates.Loading,
}))
reducer.on(actions.selectDeliverySpeedSuccess, (state, context: any) => ({
  ...state,
  ...context,
  ...(state.cart && {
    cart: {
      ...state.cart,
      ...context.cart,
    },
  }),
  delivery: {
    ...state.delivery,
    ...context.delivery,
    deliveryMethods: state.delivery.deliveryMethods,
    isCustomerSelectedSpeed: true,
  },
  deliverySpeedSelectionStatus: LoadingStates.LoadedSuccess,
  errors: (propOr as any)([], 'errors', state).filter(
    (error) => error.section !== CHECKOUT_SECTIONS.delivery,
  ),
}))
reducer.on(actions.selectDeliverySpeedFail, (state, errors) => ({
  ...state,
  deliverySpeedSelectionStatus: LoadingStates.LoadedFail,
  errors,
}))

reducer.on(actions.setPreselectedPickupPlace, (state, code) => ({
  ...state,
  delivery: {
    ...state.delivery,
    preselectedPickupPlace: code,
  },
}))

reducer.on(actions.setSelectedTimeSlot, (state, selectedTimeSlot) => ({
  ...state,
  delivery: {
    ...state.delivery,
    selectedTimeSlot,
  },
}))

// ----------------------------------------
// FETCH PICKUP POINTS
// ----------------------------------------
reducer.on(actions.resetPickupPointsState, (state) => ({
  ...state,
  pickupPointsStatus: LoadingStates.NotLoaded,
}))
reducer.on(actions.fetchPickupPointsStart, (state) => ({
  ...state,
  pickupPointsStatus: LoadingStates.Loading,
}))
reducer.on(actions.fetchPickupPointsSuccess, (state, pickupPointResponse) => {
  return {
    ...state,
    pickupPointsStatus: LoadingStates.LoadedSuccess,
    delivery: {
      ...state.delivery,
      deliveryMethods: {
        ...state.delivery.deliveryMethods,
        [DELIVERY_METHODS.pickupPoint]: {
          ...state.delivery.deliveryMethods?.[DELIVERY_METHODS.pickupPoint],
          pickupPoints: normalizePickupPlaces(pickupPointResponse),
          rawPickupPointsResponse: pickupPointResponse,
        },
      },
    },
  }
})
reducer.on(actions.fetchPickupPointsFail, (state) => ({
  ...state,
  pickupPointsStatus: LoadingStates.LoadedFail,
}))
// ----------------------------------------
// FETCH Click and Collect POINTS
// ----------------------------------------
reducer.on(actions.resetCnCPointsState, (state) => ({
  ...state,
  CnCPointsStatus: LoadingStates.NotLoaded,
}))
reducer.on(actions.fetchCnCPointsStart, (state) => ({
  ...state,
  CnCPointsStatus: LoadingStates.Loading,
}))
reducer.on(actions.fetchCnCPointsSuccess, (state, pickupPointResponse) => ({
  ...state,
  CnCPointsStatus: LoadingStates.LoadedSuccess,
  delivery: {
    ...state.delivery,
    deliveryMethods: {
      ...state.delivery.deliveryMethods,
      [DELIVERY_METHODS.clickAndCollect]: {
        ...state.delivery.deliveryMethods?.[DELIVERY_METHODS.clickAndCollect],
        CnCPoints: normalizePickupPlaces(pickupPointResponse),
      },
    },
  },
}))
reducer.on(actions.fetchCnCPointsFail, (state) => ({
  ...state,
  CnCPointsStatus: LoadingStates.LoadedFail,
}))
// ----------------------------------------
// ADDRESS HANDLING
// ----------------------------------------
reducer.on(actions.postNewAddressReset, (state) => {
  return {
    ...state,
    errors: [],
    addressRequestStatus: LoadingStates.NotLoaded,
  }
})
reducer.on(actions.postNewAddressStart, (state) => {
  return {
    ...state,
    addressRequestStatus: LoadingStates.Loading,
  }
})
reducer.on(actions.postNewAddressSuccess, (state, { cart, customer, delivery, payment }) => {
  return {
    ...state,
    cart: { ...state.cart, ...cart },
    customer: { ...state.customer, ...customer },
    ...(delivery && {
      delivery: {
        ...state.delivery,
        ...delivery,
      },
    }),
    ...(payment && {
      payment: {
        ...state.delivery,
        ...payment,
      },
    }),
    addressRequestStatus: LoadingStates.LoadedSuccess,
  }
})
reducer.on(actions.postNewAddressFail, (state, errors) => {
  return {
    ...state,
    errors,
    addressRequestStatus: LoadingStates.LoadedFail,
  }
})
reducer.on(actions.updateAddressStart, (state) => {
  return {
    ...state,
    addressRequestStatus: LoadingStates.Loading,
  }
})
reducer.on(
  actions.updateAddressSuccess,
  (state, { cart, customer, delivery = {}, payment = {} }) => {
    return {
      ...state,
      cart: { ...state.cart, ...cart },
      customer: { ...state.customer, ...customer },
      delivery: { ...state.delivery, ...delivery },
      addressRequestStatus: LoadingStates.LoadedSuccess,
      payment: {
        ...state.payment,
        ...payment,
      },
    }
  },
)
reducer.on(actions.updateAddressFail, (state, errors) => {
  return {
    ...state,
    errors,
    addressRequestStatus: LoadingStates.LoadedFail,
  }
})
reducer.on(actions.updateAddressDeliveryModes, (state, { cart, delivery = {} }: any) => {
  return {
    ...state,
    cart,
    delivery: mergeDeepRight(state.delivery, delivery),
  }
})
reducer.on(actions.setShouldShowProductsDetails, (state, shouldShowProductsDetails) => {
  return {
    ...state,
    cart: {
      ...state.cart,
      shouldShowProductsDetails,
    },
  }
})

reducer.on(actions.setHasUndeliverableItems, (state, hasUndeliverableItems) => {
  return {
    ...state,
    ui: {
      ...state.ui,
      hasUndeliverableItems,
    },
  }
})

reducer.on(actions.setSelectedHomeDeliveryAddressId, (state, selectedDeliveryAddress) => {
  return mergeDeepRight(state, {
    delivery: {
      deliveryMethods: {
        [DELIVERY_METHODS.homeDelivery]: {
          selectedDeliveryAddress,
        },
      },
    },
  })
})

reducer.on(actions.setAddressSuggestions, (state, addressSuggestions) => {
  return {
    ...state,
    delivery: {
      ...state.delivery,
      addressSuggestions,
    },
  }
})
reducer.on(actions.removeAddressSuggestions, (state) => {
  const { addressSuggestions, ...deliveryStateWithoutAddressSuggestions } = state.delivery
  return {
    ...state,
    delivery: deliveryStateWithoutAddressSuggestions,
  }
})

reducer.on(actions.showAddressSuggestions, (state) => {
  return {
    ...state,
    delivery: {
      ...state.delivery,
      addressSuggestions: {
        ...state.delivery.addressSuggestions,
        shouldShowAddressSuggestions: true,
      },
    },
  }
})
reducer.on(actions.hideAddressSuggestions, (state) => {
  return {
    ...state,
    delivery: {
      ...state.delivery,
      addressSuggestions: {
        ...state.delivery.addressSuggestions,
        shouldShowAddressSuggestions: false,
      },
    },
  }
})

reducer.on(actions.setUserEditedAddressFromSuggestions, (state) => {
  return {
    ...state,
    delivery: {
      ...state.delivery,
      addressSuggestions: {
        ...state.delivery.addressSuggestions,
        userEditedAddressFromSuggestions: true,
      },
    },
  }
})

// ----------------------------------------
// PATCH PAYMENT
// ----------------------------------------
reducer.on(actions.patchPaymentStart, (state) => {
  return {
    ...state,
    patchPaymentStatus: LoadingStates.Loading,
  }
})
reducer.on(actions.patchPaymentSuccess, (state, { newPaymentState }) => {
  return {
    ...state,
    patchPaymentStatus: LoadingStates.LoadedSuccess,
    errors: (propOr as any)([], 'errors', state).filter(
      (error) => error.section !== CHECKOUT_SECTIONS.payment,
    ),
    payment: { ...state.payment, ...newPaymentState },
  }
})
reducer.on(actions.patchPaymentFail, (state) => {
  return {
    ...state,
    patchPaymentStatus: LoadingStates.LoadedFail,
  }
})
reducer.on(actions.setSelectedPaymentMethod, (state, paymentMethod) => ({
  ...state,
  payment: {
    ...state.payment,
    selectedPaymentMethod: paymentMethod,
  },
}))
reducer.on(actions.savePaymentDetails, (state, paymentData) => ({
  ...state,
  payment: {
    ...state.payment,
    ...paymentData,
  },
}))

reducer.on(actions.updateRupayData, (state, rupayData) => ({
  ...state,
  paymentData: {
    ...state.paymentData,
    rupayData,
  },
}))

reducer.on(actions.resetRupayData, (state) => ({
  ...state,
  paymentData: {
    ...state.paymentData,
    rupayData: undefined,
  },
}))

reducer.on(actions.updateCreditGuardData, (state, creditGuardData) => ({
  ...state,
  paymentData: {
    ...state.paymentData,
    creditGuardData,
  },
}))

reducer.on(actions.resetCreditGuardData, (state) => ({
  ...state,
  paymentData: {
    ...state.paymentData,
    creditGuardData: undefined,
  },
}))

reducer.on(actions.setAvailablePaymentMethods, (state, availablePaymentMethods) => ({
  ...state,
  payment: {
    ...state.payment,
    availablePaymentMethods,
  },
}))

reducer.on(actions.setLastUsedPickupPlacePostalCode, (state, lastUsedPickupPlacePostalCode) => ({
  ...state,
  delivery: {
    ...state.delivery,
    lastUsedPickupPlacePostalCode,
  },
}))
reducer.on(actions.postProceedStart, (state) => ({
  ...state,
  postProceedStatus: LoadingStates.Loading,
  errors: [],
}))
reducer.on(actions.postProceedSuccess, (state) => ({
  ...state,
  postProceedStatus: LoadingStates.LoadedSuccess,
}))
reducer.on(actions.postProceedFail, (state) => ({
  ...state,
  postProceedStatus: LoadingStates.LoadedFail,
}))
reducer.on(actions.postProceedReset, (state) => ({
  ...state,
  postProceedStatus: LoadingStates.NotLoaded,
}))

reducer.on(actions.addGiftCardStart, (state) => ({
  ...state,
  errors: [],
  giftCardsStatus: LoadingStates.Loading,
}))
reducer.on(actions.addGiftCardSuccess, (state) => ({
  ...state,
  giftCardsStatus: LoadingStates.LoadedSuccess,
}))
reducer.on(actions.addGiftCardFail, (state) => ({
  ...state,
  giftCardsStatus: LoadingStates.LoadedFail,
}))
reducer.on(actions.removeGiftCardStart, (state, removedGiftCardNumber) => ({
  ...state,
  errors: [],
  payment: {
    ...state.payment,
    giftCardList: state.payment.giftCardList.filter(
      (card) => card.cardNumber !== removedGiftCardNumber,
    ),
  },
  giftCardsStatus: LoadingStates.Loading,
}))
reducer.on(actions.removeGiftCardSuccess, (state) => ({
  ...state,
  giftCardsStatus: LoadingStates.LoadedSuccess,
}))
reducer.on(actions.removeGiftCardFail, (state, originalGiftCardsList) => ({
  ...state,
  payment: {
    ...state.payment,
    giftCardList: originalGiftCardsList,
  },
  giftCardsStatus: LoadingStates.LoadedFail,
}))
reducer.on(actions.resetGiftCardModalData, (state) => ({
  ...state,
  giftCardsStatus: LoadingStates.NotLoaded,
}))
reducer.on(actions.removeCreditCardStart, (state, cardIdToRemove) => {
  return {
    ...state,
    removeCreditCardStatus: LoadingStates.Loading,
    payment: {
      ...state.payment,
      savedCards: state.payment.savedCards.filter((card) => card.id !== cardIdToRemove),
    },
  }
})
reducer.on(actions.removeCreditCardSuccess, (state) => {
  return {
    ...state,
    removeCreditCardStatus: LoadingStates.LoadedSuccess,
  }
})
reducer.on(actions.removeCreditCardFail, (state, savedCards) => {
  return {
    ...state,
    removeCreditCardStatus: LoadingStates.LoadedFail,
    payment: {
      ...state.payment,
      savedCards,
    },
  }
})
// ----------------------------------------
// Parcels
// ----------------------------------------
reducer.on(actions.setSplitOrderModalUI, (state, isOpen) => {
  return produce(state, (draftState) => {
    draftState.ui.parcels.isSplitOrderModalOpen = isOpen
  })
})
reducer.on(actions.setMainDeliveryModalUI, (state, isOpen) => {
  return produce(state, (draftState) => {
    draftState.ui.parcels.isMainDeliveryModalOpen = isOpen
  })
})
reducer.on(actions.setCrossbrandsDeliveryModalUI, (state, { parcel, isOpen }) => {
  return produce(state, (draftState) => {
    draftState.ui.parcels.isCrossbrandsDeliveryModalOpen = isOpen
    draftState.ui.parcels.crossbrandsDeliveryModalParcelId = parcel?.id || ''
  })
})
reducer.on(actions.setDropShipDeliveryModalUI, (state, isOpen) => {
  return produce(state, (draftState) => {
    draftState.ui.parcels.isDropShipDeliveryModalOpen = isOpen
  })
})

reducer.on(actions.setBOPISInformationModalUI, (state, isOpen) => {
  return produce(state, (draftState) => {
    draftState.ui.parcels.isBOPISInformationModalOpen = isOpen
  })
})

reducer.on(actions.setBOPISSplitOrderModalUI, (state, isOpen) => {
  return produce(state, (draftState) => {
    draftState.ui.parcels.isBOPISSplitOrderModalOpen = isOpen
  })
})

reducer.on(actions.updateDeliveryModesStart, (state): any => ({
  ...state,
  updateDeliveryModesStatus: LoadingStates.Loading,
}))

reducer.on(actions.updateDeliveryModesSuccess, (state, delivery: NormalizedDeliveryModes): any =>
  mergeDeepRight(state, {
    updateDeliveryModesStatus: LoadingStates.LoadedSuccess,
    delivery,
  }),
)

reducer.on(actions.updateDeliveryModesFail, (state, errors): any => ({
  ...state,
  updateDeliveryModesStatus: LoadingStates.LoadedFail,
  errors,
}))

reducer.on(actions.updateFetchedCities, (state, cities) => ({
  ...state,
  additionalInfo: {
    ...state.additionalInfo,
    cities,
  },
}))

reducer.on(actions.updateFetchedDistricts, (state, districts) => ({
  ...state,
  additionalInfo: {
    ...state.additionalInfo,
    districts,
  },
}))

reducer.on(actions.updateFetchedPostalCode, (state, postalCode) => ({
  ...state,
  additionalInfo: {
    ...state.additionalInfo,
    postalCode,
  },
}))

reducer.on(actions.fetchSettingsSuccess, (state, settings) => ({
  ...state,
  settings: {
    ...settings,
  },
}))

reducer.on(actions.editItemQuantityStart, (state, id) => ({
  ...state,
  editItemQuantityStatus: { status: LoadingStates.Loading, id },
}))

reducer.on(actions.editItemQuantitySuccess, (state, id) => ({
  ...state,
  editItemQuantityStatus: { status: LoadingStates.LoadedSuccess, id },
}))

reducer.on(actions.editItemQuantityFail, (state, id) => ({
  ...state,
  editItemQuantityStatus: { status: LoadingStates.LoadedFail, id },
}))
reducer.on(actions.setParcelToShowInProductDetails, (state, id) => ({
  ...state,
  parcelToShowInProductDetails: id,
}))

// Used in experiment remove on cleanup - [COPAYNOL-2027]
reducer.on(actions.setHasCompletePurchaseClicked, (state, hasCompletePurchaseClicked) => ({
  ...state,
  additionalInfo: {
    ...state.additionalInfo,
    hasCompletePurchaseClicked,
  },
}))

reducer.on(actions.setHasOpenedDeliverySection, (state) => ({
  ...state,
  additionalInfo: {
    ...state.additionalInfo,
    hasOpenedDeliverySection: true,
  },
}))

export default reducer
